import React from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronRight, Trash2 } from "lucide-react";
import { TenantStatusBadge, TenantPackageBadge } from "./TenantBadges";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function TenantTable({ items, loading, onDelete }) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 p-8 text-center text-white/40 text-sm" data-testid="tenant-table-loading">
        Memuat tenant…
      </div>
    );
  }
  if (!items?.length) {
    return (
      <div className="rounded-2xl border border-white/10 p-10 text-center text-white/40 text-sm" data-testid="tenant-table-empty">
        Belum ada tenant. Klik <strong className="text-white">+ Buat Tenant</strong> untuk menambahkan.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="tenant-table">
      <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
        <div className="col-span-4">Tenant</div>
        <div className="col-span-2">Tenant Code</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-1">Package</div>
        <div className="col-span-2">Created</div>
        <div className="col-span-1 text-right">Action</div>
      </div>

      {items.map((t) => {
        const isDeleted = !!t.deleted_at;
        return (
          <div
            key={t.tenant_id}
            className={`grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-4 border-t border-white/5 hover:bg-white/5 transition-colors ${
              isDeleted ? "opacity-50" : ""
            }`}
            data-testid={`tenant-row-${t.tenant_slug || t.tenant_id}`}
          >
            <Link to={`/platform/tenants/${t.tenant_id}`} className="col-span-4 flex items-center gap-3 min-w-0">
              <div
                className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
                style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 20%, transparent)" }}
              >
                <Building2 size={16} className="brand-text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-white truncate">{t.tenant_name}</div>
                <div className="text-[10px] text-white/40 font-mono truncate">{t.tenant_slug}</div>
              </div>
            </Link>
            <div className="col-span-2 self-center text-xs font-mono text-white/70">{t.tenant_code}</div>
            <div className="col-span-2 self-center">
              <TenantStatusBadge status={t.tenant_status} size="sm" />
            </div>
            <div className="col-span-1 self-center">
              <TenantPackageBadge package={t.package} size="sm" />
            </div>
            <div className="col-span-2 self-center text-xs text-white/50">{formatDate(t.created_at)}</div>
            <div className="col-span-1 text-right self-center flex items-center gap-1 justify-end">
              {!isDeleted && onDelete && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onDelete(t);
                  }}
                  className="w-7 h-7 rounded-lg grid place-items-center text-rose-400/70 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                  data-testid={`tenant-delete-${t.tenant_slug || t.tenant_id}`}
                  aria-label="Hapus tenant"
                >
                  <Trash2 size={13} />
                </button>
              )}
              <Link to={`/platform/tenants/${t.tenant_id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/40 hover:text-white hover:bg-white/10 transition-colors">
                <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}
